import { Autocomplete, Box, Grid, TextField, Typography } from "@mui/material";
import useApi from "_api/index";
import CustomizeDialog from "components/Car/CustomizeDialog";
import CustomizeDatePicker from "components/DatePicker";
import { GradientButtonOutline } from "components/GradientButton";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import nationalities from "data/nationalities_th.json";
import OptionProvince from "components/OptionProvince";
import { useCarBrands } from "contexts/CarBrandsContext";
import { getBrandName } from "utils/helpers/brandCar";
import * as Yup from "yup";
import AlertToast from "components/AlertToast";

const ModalContractBuyAndSell = ({
  isOpen,
  onClose,
  car,
  order = {},
  customer = {},
  document = null,
  onSuccess,
  type = "buy_car",
}) => {
  const [error, setError] = useState({
    open: false,
    status: "info",
    message: "",
  });
  useEffect(() => {
    if (isOpen && document && document.document_id) {
      fetchData();
    } else if (isOpen && customer && car) {
      let defaultValues = {
        ...formik.values,
        car_code: car.car_code || "",
        license_plate: car.license_plate || "",
        license_province: car.license_province || "",
        car_type: car.car_type || "",
        engine_brand: car.engine_brand || "",
        car_no: car.car_no || "",
        engine_number: car.engine_number || "",
        car_no: car.car_no || "",
        total_price: car.sell_price ?? car.car_price ?? "",
        contract_date: new Date(),
        payment_due_date: new Date(),
        seller_nationality: "ไทย",
        buyer_nationality: "ไทย",
      };
      if (type === "buy_car") {
        defaultValues = {
          ...defaultValues,
          seller_name: customer.customer_name || "",
          seller_age: customer.customer_age || "",
          seller_phone: customer.customer_phonenumber || "",
          seller_address: customer.customer_address || "",
          seller_nationality: customer.customer_nationality || "ไทย",
        };
      } else if (type === "sell_car") {
        defaultValues = {
          ...defaultValues,
          buyer_name: customer.customer_name || "",
          buyer_age: customer.customer_age || "",
          buyer_phone: customer.customer_phonenumber || "",
          buyer_address: customer.customer_address || "",
          buyer_idcard: customer.customer_idcard || "",
          buyer_nationality: customer.customer_nationality || "ไทย",
        };
      }
      formik.setValues(defaultValues);
      // Any side effects when modal opens
    } else if (!isOpen) {
      formik.resetForm();
    }
  }, [isOpen, car, customer, document]);

  const fetchData = async () => {
    try {
      let response = null;
      if (type === "buy_car") {
        response = await useApi.buy_car.getBuyCarContractByID(
          document.document_id
        );
      } else if (type === "sell_car") {
        response = await useApi.ready_car.getSellCarContractByID(
          document.document_id
        );
      }
      if (response.success) {
        const data = response.data;
        formik.setValues({
          ...data,
        });
      }
    } catch (error) {
      console.error("Error fetching contract buy car:", error);
    }
  };

  const { carBrands } = useCarBrands();
  const brandName = useMemo(() => {
    return car && car.car_brand_id
      ? getBrandName(car.car_brand_id, carBrands)
      : "ยี่ห้อรถยนต์";
  }, [carBrands, car]);

  const generateValidationSchema = () => {
    return Yup.object({
      contract_date: Yup.date().required("กรุณาเลือกวันที่ยื่นคำขอ"),
      seller_name: Yup.string().required("กรุณากรอกชื่อผู้ขาย"),
      seller_age: Yup.number()
        .min(18, "อายุต้องไม่น้อยกว่า 18 ปี")
        .required("กรุณากรอกอายุ"),
      seller_nationality: Yup.string().required("กรุณาเลือกสัญชาติ"),
      seller_phone: Yup.string().required("กรุณากรอกโทรศัพท์"),
      seller_address: Yup.string().required("กรุณากรอกที่อยู่"),
      buyer_name: Yup.string().required("กรุณากรอกชื่อผู้ซื้อ"),
      buyer_age: Yup.number()
        .min(18, "อายุต้องไม่น้อยกว่า 18 ปี")
        .required("กรุณากรอกอายุ"),
      buyer_phone: Yup.string().required("กรุณากรอกโทรศัพท์"),
      buyer_address: Yup.string().required("กรุณากรอกที่อยู่"),
      buyer_idcard: Yup.string().required("กรุณากรอกเลขบัตรประชาชนผู้ซื้อ"),
      total_price: Yup.number()
        .min(1, "ยอดเงินต้องมากกว่า 0")
        .required("กรุณากรอกยอดเงินทั้งหมด"),
    });
  };

  const formik = useFormik({
    initialValues: {
      contract_date: new Date(),
      seller_name: "",
      seller_age: "",
      seller_nationality: "ไทย",
      seller_phone: "",
      seller_address: "",
      car_code: "",
      license_plate: "",
      license_province: "",
      car_type: "",
      engine_brand: "",
      car_no: "",
      engine_number: "",
      buyer_name: "",
      buyer_age: "",
      buyer_nationality: "ไทย",
      buyer_phone: "",
      buyer_address: "",
      buyer_idcard: "",
      total_price: "",
      deposit_price: null,
      remaining_price: null,
      payment_due_date: new Date(),
    },
    validationSchema: generateValidationSchema(),
    onSubmit: (values) => {
      // Handle form submission
      console.log("Form values:", values);
      if (type == "buy_car") {
        // call api create contract buy and sell car
        handleSubmitContractBuyCar(values);
      } else if (type === "sell_car") {
        handleSubmitContractSellCar(values);
        // call api create contract buy and sell car for sell car
      }
    },
  });

  const onSuccessSubmit = (status) => {
    if (status) {
      setError({
        open: true,
        status: "success",
        message: "บันทึกข้อมูลสำเร็จ",
      });
      onSuccess && onSuccess();
      onClose();
    } else {
      setError({
        open: true,
        status: "error",
        message: "บันทึกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง",
      });
    }
  };

  const handleSubmitContractBuyCar = async (values) => {
    try {
      let response = null;
      if (document && document.document_id) {
        response = await useApi.buy_car.updateBuyCarContract(
          document.document_id,
          values
        );
      } else {
        response = await useApi.buy_car.createBuyCarContract(
          order.order_id,
          values
        );
      }
      if (response.success) {
        onSuccessSubmit(true);
      } else {
        console.error("Failed to submit contract buy car:", response.message);
      }
    } catch (error) {
      console.error("Error submitting contract buy car:", error);
      onSuccessSubmit(false);
    }
  };

  const handleSubmitContractSellCar = async (values) => {
    try {
      let response = null;
      if (document && document.document_id) {
        response = await useApi.ready_car.updateSellCarContract(
          document.document_id,
          values
        );
      } else {
        response = await useApi.ready_car.createSellCarContract(
          order.order_id,
          values
        );
      }
      if (response.success) {
        onSuccessSubmit(true);
      }
    } catch (error) {
      onSuccessSubmit(false);
      console.error("Error submitting contract sell car:", error);
    }
  };

  return (
    <>
      <CustomizeDialog
        open={isOpen}
        onClose={onClose}
        title={
          document
            ? "แก้ไขหนังสือสัญญาซื้อ - ขายรถ"
            : "สร้างหนังสือสัญญาซื้อ - ขายรถ"
        }
        maxWidth="md"
        fullWidth
        onSave={formik.handleSubmit}
      >
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              gap: 1,
              width: "100%",
              "& > button": {
                height: "40px",
                cursor: "default",
                pointerEvents: "none",
                background: "rgba(0, 187, 159, 0.2)",
                "&:hover": {
                  background: "transparent",
                },
                boxShadow: "none",
              },
            }}
          >
            <GradientButtonOutline>
              {car ? car.car_type : "รถยนต์"}
            </GradientButtonOutline>
            <GradientButtonOutline>
              {car ? brandName : "ยี่ห้อรถยนต์"}
            </GradientButtonOutline>
            <GradientButtonOutline>
              {car
                ? car.license_plate + " " + car.license_province
                : "ทะเบียนรถ"}
            </GradientButtonOutline>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              หนังสือสัญญาซื้อ - ขายรถ
            </Typography>

            <Grid container spacing={2} px={1}>
              <Grid item xs={12}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={2}>
                    <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                      วันที่ยื่นคำขอ
                    </Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <CustomizeDatePicker
                      value={formik.values["contract_date"]}
                      onChange={(newValue) => {
                        formik.setFieldValue("contract_date", newValue);
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>
              ข้อมูลผู้ขาย
            </Typography>
            <Grid container spacing={2} px={1} alignItems={"center"}>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ชื่อผู้ขาย",
                    name: "seller_name",
                    type: "text",
                    placeholder: "กรุณากรอกชื่อผู้ขาย",
                  }}
                  formik={formik}
                />
              </Grid>

              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  อายุ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  name="seller_age"
                  placeholder="กรุณากรอกอายุ"
                  value={formik.values["seller_age"]}
                  onChange={(e) => {
                    // limit to min and max
                    if (e.target.value === "") {
                      formik.setFieldValue("seller_age", "");
                      return;
                    }
                    let value = parseInt(e.target.value);
                    if (value > 150) value = 150;
                    formik.setFieldValue("seller_age", value);
                  }}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  InputProps={{ inputProps: { min: 18, max: 150 } }}
                  helperText={
                    formik.touched["seller_age"] && formik.errors["seller_age"]
                  }
                  error={
                    formik.touched["seller_age"] &&
                    Boolean(formik.errors["seller_age"])
                  }
                />
              </Grid>
              <Grid item xs={2}>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                >
                  สัญชาติ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  options={nationalities}
                  getOptionLabel={(option) => option.nationality}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="กรุณากรอกสัญชาติ"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                    />
                  )}
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      "seller_nationality",
                      newValue.nationality
                    );
                  }}
                  value={
                    nationalities.find(
                      (n) => n.nationality === formik.values.seller_nationality
                    ) || null
                  }
                  disableClearable
                />
              </Grid>

              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "โทรศัพท์",
                    name: "seller_phone",
                    type: "text",
                    placeholder: "กรุณากรอกโทรศัพท์",
                  }}
                  formik={formik}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ที่อยู่",
                    name: "seller_address",
                    type: "text",
                    placeholder: "กรุณากรอกที่อยู่",
                  }}
                  formik={formik}
                />
              </Grid>
            </Grid>

            <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>
              ข้อมูลรถ
            </Typography>
            <Grid container spacing={2} px={1} alignItems={"center"}>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "รหัสรถ",
                    name: "car_code",
                    type: "text",
                    placeholder: "กรุณากรอกรหัสรถ",
                  }}
                  formik={formik}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ทะเบียน
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="text"
                  name="license_plate"
                  placeholder="กรุณากรอกทะเบียน"
                  value={formik.values["license_plate"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
              <Grid item xs={1}>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                >
                  จังหวัด
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <OptionProvince
                  value={formik.values["license_province"]}
                  onChangeValue={(newValue) => {
                    formik.setFieldValue("license_province", newValue);
                  }}
                  placeholder="กรุณากรอกจังหวัด"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>

              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ชนิดรถ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="text"
                  name="car_type"
                  placeholder="กรุณากรอกชนิดรถ"
                  value={formik.values["car_type"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  เลขตัวรถ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="text"
                  name="car_no"
                  placeholder="กรุณากรอกเลขตัวรถ"
                  value={formik.values["car_no"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>

              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  ชนิดเครื่องยนต์
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="text"
                  name="engine_brand"
                  placeholder="กรุณากรอกชนิดเครื่องยนต์"
                  value={formik.values["engine_brand"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  เลขเครื่องยนต์
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="text"
                  name="engine_number"
                  placeholder="กรุณากรอกเลขเครื่องยนต์"
                  value={formik.values["engine_number"]}
                  onChange={formik.handleChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  disabled
                />
              </Grid>
            </Grid>

            <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>
              ข้อมูลผู้ซื้อ
            </Typography>
            <Grid container spacing={2} px={1} alignItems={"center"}>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ชื่อผู้ซื้อ",
                    name: "buyer_name",
                    type: "text",
                    placeholder: "กรุณากรอกชื่อผู้ซื้อ",
                  }}
                  formik={formik}
                />
              </Grid>
              <Grid item xs={2}>
                <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                  อายุ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  type="number"
                  name="buyer_age"
                  placeholder="กรุณากรอกอายุ"
                  value={formik.values["buyer_age"]}
                  onChange={(e) => {
                    // limit to min and max
                    if (e.target.value === "") {
                      formik.setFieldValue("buyer_age", "");
                      return;
                    }
                    let value = parseInt(e.target.value);
                    if (value > 150) value = 150;
                    formik.setFieldValue("buyer_age", value);
                  }}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white",
                      height: "40px",
                    },
                  }}
                  InputProps={{ inputProps: { min: 18, max: 150 } }}
                  helperText={
                    formik.touched["buyer_age"] && formik.errors["buyer_age"]
                  }
                  error={
                    formik.touched["buyer_age"] &&
                    Boolean(formik.errors["buyer_age"])
                  }
                />
              </Grid>
              <Grid item xs={2}>
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "nowrap", textAlign: "center" }}
                >
                  สัญชาติ
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Autocomplete
                  options={nationalities}
                  getOptionLabel={(option) => option.nationality}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="กรุณากรอกสัญชาติ"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                    />
                  )}
                  value={
                    nationalities.find(
                      (n) => n.nationality === formik.values.buyer_nationality
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    formik.setFieldValue(
                      "buyer_nationality",
                      newValue?.nationality
                    );
                  }}
                  disableClearable
                />
              </Grid>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "โทรศัพท์",
                    name: "buyer_phone",
                    type: "text",
                    placeholder: "กรุณากรอกโทรศัพท์",
                  }}
                  formik={formik}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ที่อยู่",
                    name: "buyer_address",
                    type: "text",
                    placeholder: "กรุณากรอกที่อยู่",
                  }}
                  formik={formik}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "เลขบัตรประชาชน",
                    name: "buyer_idcard",
                    type: "text",
                    placeholder: "กรุณากรอกเลขบัตรประชาชนผู้ซื้อ",
                  }}
                  formik={formik}
                  inputProps={{
                    maxLength: 13,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              </Grid>
            </Grid>

            <Typography variant="h5" sx={{ mb: 1, mt: 2 }}>
              ข้อมูลซื้อ - ขาย
            </Typography>
            <Grid container spacing={2} px={1} alignItems={"center"}>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ยอดเงินทั้งหมด",
                    name: "total_price",
                    type: "number",
                    placeholder: "กรุณากรอกยอดเงินทั้งหมด",
                  }}
                  formik={formik}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ยอดเงินมัดจำ",
                    name: "deposit_price",
                    type: "number",
                    placeholder: "กรุณากรอกยอดเงินทั้งหมด",
                  }}
                  formik={formik}
                />
              </Grid>
              <Grid item xs={12}>
                <FieldRow
                  field={{
                    label: "ยอดเงินคงค้าง",
                    name: "remaining_price",
                    type: "number",
                    placeholder: "กรุณากรอกยอดเงินทั้งหมด",
                  }}
                  formik={formik}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={2}>
                    <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
                      ชำระภายในวันที่
                    </Typography>
                  </Grid>
                  <Grid item xs={10}>
                    <CustomizeDatePicker
                      value={formik.values["payment_due_date"]}
                      onChange={(newValue) => {
                        formik.setFieldValue("payment_due_date", newValue);
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "white",
                          height: "40px",
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </form>
      </CustomizeDialog>
      <AlertToast
        open={error.open}
        message={error.message}
        type={error.status}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setError({ open: false, status: "info", message: "" })}
      />
    </>
  );
};

const FieldRow = ({ field, formik, ...props }) => (
  <Grid container alignItems="center" spacing={1}>
    <Grid item xs={2}>
      <Typography variant="body1" sx={{ whiteSpace: "nowrap" }}>
        {field.label}
      </Typography>
    </Grid>
    <Grid item xs={10}>
      {field.type === "text" || field.type === "number" ? (
        <TextField
          {...props}
          fullWidth
          variant="outlined"
          type={field.type}
          name={field.name}
          placeholder={field.placeholder}
          value={formik.values[field.name]}
          onChange={formik.handleChange}
          sx={{
            "& .MuiOutlinedInput-root": { bgcolor: "white", height: "40px" },
          }}
          onBlur={formik.handleBlur}
          error={
            formik.touched[field.name] && Boolean(formik.errors[field.name])
          }
          helperText={formik.touched[field.name] && formik.errors[field.name]}
        />
      ) : (
        <Autocomplete
          options={nationalities}
          getOptionLabel={(option) => option.nationality}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={field.placeholder}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: "white",
                  height: "40px",
                },
              }}
            />
          )}
          {...props}
        />
      )}
    </Grid>
  </Grid>
);

export default ModalContractBuyAndSell;
